/**
 * ImpersonationBanner — BOLT-025.
 *
 * Sticky full-width warning bar rendered at the top of AppShell when the
 * current access token carries an `impersonator_id` claim. Returns null
 * when not in an impersonation session so it has zero layout cost.
 *
 * "End Session" opens a ConfirmDialog, then:
 *   1. POSTs /v1/auth/end-impersonation (uses the current impersonation token).
 *   2. Restores the stashed super-admin token from sessionStorage.
 *   3. Navigates to /admin/orgs (or /login if the stash is absent).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AppBar, Box, Button, Toolbar, Typography } from '@mui/material';
import { useImpersonationState } from '@/hooks/useImpersonationState.js';
import { useAuthStore } from '@/store/auth.js';
import { restoreSuperAdminToken } from '@/auth/impersonation.js';
import { apiClient, ApiError } from '@/lib/api-client.js';
import { queryClient } from '@/lib/query-client.js';
import { useToast } from '@/hooks/useToast.js';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog.js';

export function ImpersonationBanner() {
  const { isImpersonating, targetEmail } = useImpersonationState();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setTargetEmail = useAuthStore((s) => s.setTargetEmail);
  const setImpersonationSessionId = useAuthStore((s) => s.setImpersonationSessionId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { toast } = useToast();

  if (!isImpersonating) return null;

  const handleEndSession = async () => {
    setEnding(true);
    try {
      await apiClient.post('/v1/auth/end-impersonation');
    } catch (err) {
      // 4xx: the server processed the request — session was already ended or
      // expired. Safe to restore locally.
      // Network error or 5xx: the session may still be active on the server.
      // Stay impersonating and let the user retry — restoring locally would be
      // safe for this tab (BOLT-403: no ambient cookie to re-enter through) but
      // it would leave a live session running unnoticed for up to an hour.
      if (!(err instanceof ApiError && err.status < 500)) {
        setEnding(false);
        setConfirmOpen(false);
        toast('Failed to end session. Please try again.', 'error');
        return;
      }
    }

    // Clear all cached server state before switching identity — prevents the
    // impersonated user's data from flashing in the restored super-admin session.
    // cancelQueries first so in-flight requests cannot repopulate the cache
    // under the incoming identity after clear() empties it (ADV-004).
    await queryClient.cancelQueries();
    queryClient.clear();

    // BOLT-403: drop the tab's session id FIRST — after this, the tab's silent
    // refresh can only speak for the identity we restore below.
    setImpersonationSessionId(null);

    const stash = restoreSuperAdminToken();
    if (stash) {
      setAuth(stash.accessToken, stash.orgId, stash.userId, stash.role);
      setTargetEmail(null);
      navigate('/admin/orgs');
    } else {
      // Stash absent (e.g. a duplicated tab whose twin consumed it) — fall
      // back to login; the cookie session bootstraps the super-admin cleanly.
      clearAuth();
      navigate('/login');
    }

    setEnding(false);
    setConfirmOpen(false);
  };

  const displayName = targetEmail ?? 'Unknown user';

  return (
    <>
      <AppBar
        position="sticky"
        color="warning"
        elevation={0}
        sx={{ zIndex: (t) => t.zIndex.appBar + 1 }}
      >
        <Toolbar variant="dense" disableGutters sx={{ px: 2, gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
              Impersonating:{' '}
            </Typography>
            <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" component="span" sx={{ ml: 1, opacity: 0.8 }}>
              — All actions are logged with your identity.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setConfirmOpen(true)}
          >
            End Session
          </Button>
        </Toolbar>
      </AppBar>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleEndSession()}
        title="End impersonation session?"
        description={`This will end your session as ${displayName} and return you to your super-admin account.`}
        confirmLabel="End Session"
        loading={ending}
      />
    </>
  );
}
